/**
 * The component list as a real .xlsx workbook.
 *
 * A flat sheet with one header row: no group headings, no merged cells and no
 * totals row, so it sorts, filters and pivots directly. The group each part
 * belongs to is a column of its own rather than a heading.
 *
 * A real workbook rather than CSV on purpose: Excel reads CSV against the
 * machine's locale, and on a Norwegian install a comma-separated file with
 * decimal points is misparsed. Numbers written as numbers avoid that entirely.
 */

import writeXlsxFile from "write-excel-file/browser";
import { fail, ok, type Result } from "./result.ts";

/** Structurally what a BOM row provides — declared here so this stays engine-free. */
export type ComponentRow = Readonly<{
  group: string;
  name: string;
  art: string;
  /** Weight of a single unit. */
  kg: number;
  n: number;
}>;

const HEADER = { fontWeight: "bold", backgroundColor: "#dce8f7", align: "left" } as const;

const COLUMNS = [
  {
    header: { value: "Type", ...HEADER },
    cell: (row: ComponentRow) => ({ value: row.group, type: String }),
    width: 14,
  },
  {
    header: { value: "Component", ...HEADER },
    cell: (row: ComponentRow) => ({ value: row.name, type: String }),
    width: 42,
  },
  {
    header: { value: "Art. no.", ...HEADER },
    cell: (row: ComponentRow) => ({ value: row.art, type: String }),
    width: 12,
  },
  {
    header: { value: "Qty", ...HEADER },
    cell: (row: ComponentRow) => ({ value: row.n, type: Number, format: "0" }),
    width: 8,
  },
  {
    header: { value: "Weight kg", ...HEADER },
    cell: (row: ComponentRow) => ({ value: row.kg, type: Number, format: "0.00" }),
    width: 12,
  },
  {
    header: { value: "Total kg", ...HEADER },
    cell: (row: ComponentRow) => ({ value: row.kg * row.n, type: Number, format: "0.0" }),
    width: 12,
  },
];

export async function componentListToXlsx(rows: readonly ComponentRow[]): Promise<Result<Blob>> {
  if (rows.length === 0) {
    return fail("Nothing to export — the component list is empty");
  }

  try {
    const blob = await writeXlsxFile([...rows], {
      columns: COLUMNS,
      sheet: "Component list",
      stickyRowsCount: 1,
    }).toBlob();

    return ok(blob);
  } catch (err) {
    const msg = err instanceof Error ? err.message : String(err);

    return fail(`Could not build the workbook: ${msg}`, err);
  }
}
